import { useHandler } from "@redotech/react-util/hook";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import {
  rcoAddressPropertiesSegmentSchema,
  RcoAddressPropertiesSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  AddressPropertyField,
  SegmentStringOperator,
} from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { OperatorSelector } from "@redotech/redo-web/conversion/operator-selector";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

const addressPropertyFieldToText: Record<AddressPropertyField, string> = {
  [AddressPropertyField.ADDRESS1]: "Address 1",
  [AddressPropertyField.ADDRESS2]: "Address 2",
  [AddressPropertyField.CITY]: "City",
  [AddressPropertyField.PROVINCE]: "State / province",
  [AddressPropertyField.POSTAL_CODE]: "Postal code",
  [AddressPropertyField.COUNTRY]: "Country",
};

const segmentStringOperatorToText: Record<SegmentStringOperator, string> = {
  [SegmentStringOperator.EQUALS]: "equals",
  [SegmentStringOperator.NOT_EQUALS]: "does not equal",
  [SegmentStringOperator.CONTAINS]: "contains",
  [SegmentStringOperator.NOT_CONTAINS]: "does not contain",
};

function formatRule(rule: {
  field: AddressPropertyField;
  operator: SegmentStringOperator;
  value: string;
}): string {
  return `${addressPropertyFieldToText[rule.field]} ${segmentStringOperatorToText[rule.operator]} "${rule.value}"`;
}

export const AddressPropertiesInspectorContent = memo(
  function AddressPropertiesInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const segment = useMemo(
      () => rcoAddressPropertiesSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          All conditions must be true
        </Text>
        {segment.rules.map((rule, index) => (
          <Text
            fontSize="xs"
            fontWeight="medium"
            key={index}
            textColor="primary"
          >
            {formatRule(rule)}
          </Text>
        ))}
      </Flex>
    );
  },
);

export const EditableAddressPropertiesInspectorContent = memo(
  function EditableAddressPropertiesInspectorContent({
    step,
    setStep,
  }: {
    step: PartialWithRequiredType<RcoSegmentStep>;
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const segment = useMemo(
      () => rcoAddressPropertiesSegmentSchema.parse(step.segment),
      [step],
    );

    const updateRules = useHandler(
      (rules: RcoAddressPropertiesSegmentSchema["rules"]) => {
        const updatedSegment: RcoAddressPropertiesSegmentSchema = {
          ...segment,
          rules,
        };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleFieldChange = useHandler(
      (index: number, field: AddressPropertyField) => {
        updateRules(
          segment.rules.map((rule, i) =>
            i === index ? { ...rule, field } : rule,
          ),
        );
      },
    );

    const handleOperatorChange = useHandler(
      (index: number, operator: SegmentStringOperator) => {
        updateRules(
          segment.rules.map((rule, i) =>
            i === index ? { ...rule, operator } : rule,
          ),
        );
      },
    );

    const handleValueChange = useHandler((index: number, value: string) => {
      updateRules(
        segment.rules.map((rule, i) =>
          i === index ? { ...rule, value } : rule,
        ),
      );
    });

    const handleAddRule = useHandler(() => {
      updateRules([
        ...segment.rules,
        {
          field: AddressPropertyField.ADDRESS1,
          operator: SegmentStringOperator.CONTAINS,
          value: "",
        },
      ]);
    });

    const handleRemoveRule = useHandler((index: number) => {
      if (segment.rules.length <= 1) {
        return;
      }
      updateRules(segment.rules.filter((_, i) => i !== index));
    });

    return (
      <Flex align="flex-start" dir="column" gap="md" w="full">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          All conditions must be true
        </Text>
        {segment.rules.map((rule, index) => (
          <Flex
            bgColor="primary"
            borderColor="secondary"
            borderStyle="solid"
            borderWidth="1px"
            dir="column"
            gap="sm"
            key={index}
            p="md"
            radius="xs"
            w="full"
          >
            <Flex align="center" dir="row" gap="sm" justify="space-between">
              <Flex align="center" dir="row" gap="sm" wrap="wrap">
                <OperatorSelector<AddressPropertyField>
                  availableOptions={Object.values(AddressPropertyField)}
                  onOperatorSelected={(field) =>
                    handleFieldChange(index, field)
                  }
                  operatorToText={addressPropertyFieldToText}
                  selectedOperator={rule.field}
                />
                <OperatorSelector<SegmentStringOperator>
                  availableOptions={Object.values(SegmentStringOperator)}
                  onOperatorSelected={(operator) =>
                    handleOperatorChange(index, operator)
                  }
                  operatorToText={segmentStringOperatorToText}
                  selectedOperator={rule.operator}
                />
              </Flex>
              <Flex shrink={0}>
                <RedoButton
                  disabled={segment.rules.length <= 1}
                  hierarchy="tertiary"
                  IconLeading={TrashIcon}
                  onClick={() => handleRemoveRule(index)}
                  size="xs"
                  theme="destructive"
                />
              </Flex>
            </Flex>
            <RedoTextInput
              fullWidth
              placeholder="Value"
              setValue={(value) => handleValueChange(index, value)}
              size="sm"
              value={rule.value}
            />
          </Flex>
        ))}
        <RedoButton
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={handleAddRule}
          size="sm"
          text="Add condition"
        />
      </Flex>
    );
  },
);
